import _noop from 'lodash/noop';
import _map from 'lodash/map';
import _once from 'lodash/once';

import { ORDER_TYPE_DELIVERY, ORDER_TYPE_PICKUP } from 'shared/constants';
import { maybePushEcommerceToDatalayer, setAnalyticsContext } from 'utils/ecommerce-analytics';
import { pushGTMDataLayer } from 'hooks/use-google-tag-manager';
import { getDispensaryGaV4Client } from 'utils/analytics/clients/dispensary-ga-v4-client';
import { isGaV4Id } from 'utils/analytics/helpers/is-ga-v4-id';
import { isBrowser } from 'utils/misc-utils';
import {
  cartItemContext,
  cartItemToGTMCartItem,
  ecommerceGTMMetaData,
  getCheckoutToken,
  getCommonEcommerceContext,
  getOrderDiscount,
  getOrderTotalTax,
  getPurchaseType,
  getUserContext,
} from './internal-gtm-tracker/helpers/internal-gtm-converters';
import { ContextCache, GA4_EVENTS } from './internal-gtm-tracker/internal-gtm-tracker.types';
import { AnalyticsContext, EventTracker } from '../events';
import { CUSTOMER_GA4_SPA_PAGE_VIEWS_FLAG, GTM_V2_FLAG } from '../constants/feature-flags';

/** Adapt an AnalyticsContext to the ContextCache shape expected by converter functions */
function toContextCache(context: AnalyticsContext): ContextCache {
  return {
    ...context,
    activePathHasChanged: false,
    activeMenuVariant: context.activeMenuVariant ?? '',
  };
}

/** Compute numeric value from a single GTM item (price * quantity) */
function itemLevelValue(item: { price?: number | string; quantity?: number | string }): number {
  return parseFloat(((Number(item.price) || 0) * (Number(item.quantity) || 1)).toFixed(2));
}

function hasConfiguredGTMValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (!value || typeof value !== 'object') return false;

  return Object.values(value).some((entry) => typeof entry === 'string' && entry.trim().length > 0);
}

function hasCustomerGTM(context: AnalyticsContext): boolean {
  const config = context.activeDispensary?.googleTagManager;

  return [config?.gtmID, config?.containerIds, config?.serverContainerUrl, config?.serverContainerUrls].some(
    hasConfiguredGTMValue
  );
}

export function createDispensaryGTMTracker(): EventTracker {
  // The customer container owns the initial page view.
  let lastGTMPageLocation = isBrowser() ? window.location.href : null;

  return {
    onContextChange: (_updates, newContext, oldContext) => {
      setAnalyticsContext(newContext);

      const pagePath = newContext.activePath?.concrete;
      if (
        !newContext.featureFlags[CUSTOMER_GA4_SPA_PAGE_VIEWS_FLAG] ||
        !pagePath ||
        pagePath === oldContext.activePath?.concrete
      )
        return;

      const client = getDispensaryGaV4Client();
      const useDirectGA4 =
        !newContext.activeDispensary || isGaV4Id(newContext.activeDispensary.googleAnalyticsID ?? null);

      if (useDirectGA4) {
        if (!client.isCapturingEcommercePayload()) return;

        client.pageView({
          pageLocation: window.location.href,
          pagePath,
          pageTitle: document.title,
        });
        return;
      }

      if (!hasCustomerGTM(newContext)) return;

      const pageLocation = window.location.href;
      if (pageLocation === lastGTMPageLocation) return;

      lastGTMPageLocation = pageLocation;
      pushGTMDataLayer({ event: 'page_view_spa', page_location: pageLocation });
    },

    cartUpdated: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const items = _map(payload.products ?? [], (item) => ({
        ...cartItemContext(context, cache),
        ...cartItemToGTMCartItem(item, true),
      }));

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.cartUpdated,
        checkout_id: getCheckoutToken(context, cache),
        delivery_method: payload.deliveryMethod,
        payment_type: payload.paymentType,
        purchase_type: payload.purchaseType,
        is_scheduled: payload.isScheduled,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache: cache, useV2: true }),
          ...getCommonEcommerceContext(context, cache),
          items,
        },
      });
    },

    cartViewed: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const items = _map(payload.products, (item) => ({
        ...cartItemContext(context, cache, payload),
        ...cartItemToGTMCartItem({ product: item }, true),
      }));

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.viewCart,
        checkout_id: getCheckoutToken(context, cache),
        purchase_type: getPurchaseType(context, cache),
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache: cache, useV2: true }),
          ...getCommonEcommerceContext(context, cache),
          items,
        },
      });
    },

    viewedProduct: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const item = {
        ...cartItemToGTMCartItem(payload, true),
        ...cartItemContext(context, cache),
      };
      const ecommMeta = ecommerceGTMMetaData({ context, contextCache: cache, useV2: true });

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.viewItem,
        ecommerce: {
          ...ecommMeta,
          value: ecommMeta.value ?? itemLevelValue(item),
          items: [item],
        },
      });
    },

    addedProductToCart: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const item = cartItemToGTMCartItem(payload, true);
      const ecommMeta = ecommerceGTMMetaData({ context, contextCache: cache, useV2: true });

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.addToCart,
        checkout_id: getCheckoutToken(context, cache),
        purchase_type: getPurchaseType(context, cache),
        ecommerce: {
          ...ecommMeta,
          value: itemLevelValue(item),
          ...getCommonEcommerceContext(context, cache),
          items: [item],
        },
      });
    },

    removedProductFromCart: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const item = cartItemToGTMCartItem(payload, true);
      const ecommMeta = ecommerceGTMMetaData({ context, contextCache: cache, useV2: true });

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.removeFromCart,
        checkout_id: getCheckoutToken(context, cache),
        purchase_type: getPurchaseType(context, cache),
        ecommerce: {
          ...ecommMeta,
          value: itemLevelValue(item),
          ...getCommonEcommerceContext(context, cache),
          items: [item],
        },
      });
    },

    viewedCheckout: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const items = _map(context.activeOrder?.cart, (cartItem) => ({
        ...cartItemContext(context, cache),
        ...cartItemToGTMCartItem(cartItem, true),
      }));

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.beginCheckout,
        checkout_id: getCheckoutToken(context, cache),
        ...getUserContext(context, cache),
        is_kiosk_order: context.isKioskOrder,
        purchase_type: getPurchaseType(context, cache),
        payment_type: context.activeOrder?.paymentMethod ?? cache.activeOrder?.paymentMethod ?? null,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache: cache, useV2: true }),
          ...getCommonEcommerceContext(context, cache),
          tax: payload.tax,
          total_tax: payload.tax,
          items,
        },
      });
    },

    placedOrder: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const items = _map(context.activeOrder?.cart, (cartItem) => ({
        ...cartItemContext(context, cache),
        ...cartItemToGTMCartItem(cartItem, true),
      }));

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.purchase,
        checkout_id: getCheckoutToken(context, cache),
        order_id: payload.order?.orderId ?? undefined,
        is_kiosk_order: context.isKioskOrder,
        purchase_type: payload.order?.medicalOrder ? 'medicinal' : 'recreational',
        payment_type: payload.order?.paymentMethod ?? null,
        delivery_method: payload.order?.deliveryInfo?.deliveryOption ? ORDER_TYPE_DELIVERY : ORDER_TYPE_PICKUP,
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
        ecommerce: {
          transaction_id: payload.order?.orderId,
          order_id: payload.order?.orderId ?? undefined,
          checkout_id: getCheckoutToken(context, cache),
          tax: payload.order?.taxAmount,
          total_tax: getOrderTotalTax(payload.order),
          shipping: payload.order?.deliveryFee ?? null,
          coupon: context.activeOrder?.coupon?.code ?? null,
          discount: getOrderDiscount(payload.order),
          ...ecommerceGTMMetaData({ context, contextCache: cache, useV2: true }),
          items,
        },
      });
    },

    accountLogin: _noop,
    buyItAgainImpression: _noop,
    buyItAgainViewAllClicked: _noop,
    loyaltySignup: _noop,
    searchedForProduct: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.search,
        search_term: payload.query,
      });
    },

    searchResults: (payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.viewSearchResults,
        search_term: payload.query,
        ecommerce: {
          items: _map(payload.products ?? [], (p) => cartItemToGTMCartItem({ product: p }, true)),
        },
      });
    },
    checkoutStarted: _noop,
    checkoutSession: _once((payload, context) => {
      if (!context.featureFlags[GTM_V2_FLAG]) return;
      const cache = toContextCache(context);
      const items = _map(context.activeOrder?.cart, (cartItem) => ({
        ...cartItemContext(context, cache),
        ...cartItemToGTMCartItem(cartItem, true),
      }));

      maybePushEcommerceToDatalayer({
        event: GA4_EVENTS.checkoutSession,
        checkout_id: payload.checkoutToken,
        ...getUserContext(context, cache),
        purchase_type: getPurchaseType(context, cache),
        payment_type: context.activeOrder?.paymentMethod ?? cache.activeOrder?.paymentMethod ?? null,
        ecommerce: {
          ...ecommerceGTMMetaData({ context, contextCache: cache, useV2: true }),
          ...getCommonEcommerceContext(context, cache),
          tax: payload.tax,
          items,
        },
      });
    }),
    productClicked: _noop,
    imageBannerClicked: _noop,

    // DutchiePay Enrollment
    paymentsError: _noop,
    dutchiePayEnrollmentButtonClicked: _noop,
    dutchiePayCTAButtonClicked: _noop,
    dutchiePayRelinkButtonViewed: _noop,
    dutchiePayRelinkButtonClicked: _noop,
    dutchiePayUnlinkRemoveCtaClicked: _noop,
    dutchiePayUnlinkModalViewed: _noop,
    dutchiePayUnlinkConfirmed: _noop,
    dutchiePayUnlinkCancelled: _noop,
    dutchiePayUnlinkSuccess: _noop,
    dutchiePayUnlinkError: _noop,
    dutchiePayEnrollmentStep: _noop,
    dutchiePayEnrollmentStepCancelled: _noop,
    dutchiePayPlaidOnEvent: _noop,
    dutchiePayPlaidOnLoad: _noop,
    dutchiePayServiceFeeShown: _noop,
    dutchiePayIncentiveExposure: _noop,
    dutchiePayPaymentMethodSelected: _noop,
    dutchiePayPlaidOnSuccess: _noop,
    dutchiePayPlaidOnExit: _noop,
    dutchiePayAeroSyncOnEvent: _noop,
    dutchiePayPreAuthEvent: _noop,
    dutchiePayAeroSyncOnLoad: _noop,
    dutchiePayAeroSyncOnClose: _noop,
    dutchiePayAeroSyncOnSuccess: _noop,
    dutchiePayAeroSyncOnError: _noop,
    // DuthchiePay Instore
    dutchiePayInstoreAccountLinkViewed: _noop,
    dutchiePayInstoreLoginViewed: _noop,
    dutchiePayInstoreCartViewed: _noop,
    dutchiePayInstoreCartApprovalClicked: _noop,
    dutchiePayInstoreCartApprovalSucceeded: _noop,
    dutchiePayInstoreCartApprovalFailed: _noop,
    sponsoredProductImpression: _noop,
    sponsoredProductClicked: _noop,
    sponsoredBannerImpression: _noop,
    sponsoredBannerClicked: _noop,
    privacyPreferenceUpdated: _noop,
    gaGTMClickEvent: _noop,
    dispensaryClicked: _noop,
    dutchiePayKioskQRCodeModalViewed: _noop,
    viewedKioskCheckoutModal: _noop,
    loginNudge: _noop,
    loginGateShown: _noop,
    loginEntryPointClicked: _noop,
    dutchiePayCheckoutFlowAction: _noop,
    roundupForCharityViewed: _noop,
    roundupForCharityToggled: _noop,
    walletPass: _noop,
    dutchiePayLinkToPayInitiated: _noop,
    dutchiePayLinkToPayIneligible: _noop,
    dutchiePayEcommModalPaymentSwitchViewed: _noop,
    dutchiePayEcommModalPaymentSwitchClicked: _noop,
    dutchiePayEcommModalPaymentSwitchOutcome: _noop,
    dutchiePayLoyaltyCtaClicked: _noop,
    productRatingViewed: _noop,
    productReviewsViewed: _noop,
    productReviewAiSummaryViewed: _noop,
    productReviewStatusChecked: _noop,
    productReviewCtaClicked: _noop,
    productReviewSubmitted: _noop,
    instoreEnrollmentStepViewed: _noop,
    instoreEnrollmentStepCompleted: _noop,
    instoreEnrollmentStepSkipped: _noop,
    medCardSignupCompleted: _noop,
    medCardSignupFailed: _noop,
    accountSignupAction: _noop,
    loyaltyEnrollmentAction: _noop,
    phoneVerificationNudgeAction: _noop,
    onboardingModalStep: _noop,
    onboardingFlowStarted: _noop,
    conciergeEntryPointClicked: _noop,
    conciergeImpression: _noop,
    conciergeAction: _noop,
    conciergeMessageSent: _noop,
    conciergeExited: _noop,
    conciergeBundleAdded: _noop,
    conciergeAccessGate: _noop,
    conciergeConversationRefreshed: _noop,
    conciergeMessageStopped: _noop,
    conciergeError: _noop,
    conciergeIdleWarning: _noop,
  };
}
